/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import { OneLineGuideCell } from '../../../core/entities';
import { Channel } from '../../../core/entities/Channel';
import { InteractiveComponents } from '../../../core/enums';
import { GuideError } from '../../../core/error';
import { TOfferPreview } from '../../../core/types';
import { DateTime } from '../../../core/utils';
import {
    SET_ONE_LINE_GUIDE_ACTIVE,
    SET_ONE_LINE_GUIDE_ARE_CHANNELS_SELECTED,
    SET_ONE_LINE_GUIDE_ARE_FUTURE_CELLS_RECEIVED,
    SET_ONE_LINE_GUIDE_ARE_PAST_CELLS_RECEIVED,
    SET_ONE_LINE_GUIDE_CELL_AIRING_NOW,
    SET_ONE_LINE_GUIDE_CELL_SELECTED,
    SET_ONE_LINE_GUIDE_CELLS_APPEND,
    SET_ONE_LINE_GUIDE_CELLS_DELETE,
    SET_ONE_LINE_GUIDE_CELLS_PREPEND,
    SET_ONE_LINE_GUIDE_CHANNELS,
    SET_ONE_LINE_GUIDE_CURRENT_OFFER_PREVIEW,
    SET_ONE_LINE_GUIDE_CURRENT_SELECTED_CELL_STAR_RATING,
    SET_ONE_LINE_GUIDE_CURRENTLY_AIRING_CHANNEL,
    SET_ONE_LINE_GUIDE_EMPTY,
    SET_ONE_LINE_GUIDE_ERROR,
    SET_ONE_LINE_GUIDE_HIDDEN,
    SET_ONE_LINE_GUIDE_HORIZONTAL_SCROLL,
    SET_ONE_LINE_GUIDE_INACTIVE,
    SET_ONE_LINE_GUIDE_IS_CONTENT_FETCHED,
    SET_ONE_LINE_GUIDE_IS_FUTURE_MODE,
    SET_ONE_LINE_GUIDE_SELECTED_CHANNEL,
    SET_ONE_LINE_GUIDE_TIME_STAMP,
    SET_ONE_LINE_GUIDE_TURBO_SCROLL,
    UPDATE_ONE_LINE_GUIDE_AIRING_NOW_TIMEOUT,
    UPDATE_ONE_LINE_GUIDE_INACTIVITY_TIMEOUT,
    UPDATE_ONE_LINE_GUIDE_STICKINESS_TIMEOUT,
} from './onelineguide.types';

export const setOneLineGuideActiveAction = (): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_ACTIVE,
    payload: {},
});

export const setOneLineGuideCellsAppendAction = (
    cells: OneLineGuideCell[],
    isPastMode: boolean,
): AnyAction => ({
    type: isPastMode ? SET_ONE_LINE_GUIDE_CELLS_PREPEND : SET_ONE_LINE_GUIDE_CELLS_APPEND,
    payload: { cells },
});

export const setOneLineGuideCellAiringNowAction = (cell: OneLineGuideCell): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_CELL_AIRING_NOW,
    payload: { cell },
});

export const setOneLineGuideCellSelectedAction = (cell: OneLineGuideCell): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_CELL_SELECTED,
    payload: { cell },
});

export const setOneLineGuideCurrentSelectedCellStarRatingAction = (
    rating: number | null,
): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_CURRENT_SELECTED_CELL_STAR_RATING,
    payload: { rating },
});

export const setOneLineGuideCurrentlyAiringChannelAction = (channel: Channel): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_CURRENTLY_AIRING_CHANNEL,
    payload: { channel },
});

export const setOneLineGuideChannelsAction = (channels: Channel[]): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_CHANNELS,
    payload: { channels },
});

export const setOneLineGuideInactiveAction = (): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_INACTIVE,
    payload: {},
});

export const setOneLineGuideCellsDeleteAction = (): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_CELLS_DELETE,
    payload: {},
});

export const setOneLineGuideAreChannelsSelectedAction = (areChannelsSelected): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_ARE_CHANNELS_SELECTED,
    payload: { areChannelsSelected },
});

export const setOneLineGuideEmptyAction = (isEmpty: boolean) => ({
    type: SET_ONE_LINE_GUIDE_EMPTY,
    payload: { isEmpty },
});

export const setOneLineGuideError = (error: GuideError | null) => ({
    type: SET_ONE_LINE_GUIDE_ERROR,
    payload: { error },
});

export const setOneLineGuideIsContentFetched = (isContentFetched): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_IS_CONTENT_FETCHED,
    payload: { isContentFetched },
});

export const setOneLineGuideIsFutureModeAction = (isFutureMode): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_IS_FUTURE_MODE,
    payload: { isFutureMode },
});

export const setOneLineGuideHidden = (): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_HIDDEN,
    payload: {},
});

export const setOneLineGuideSelectedChannelAction = (selectedChannel: Channel): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_SELECTED_CHANNEL,
    payload: { selectedChannel },
});

export const setOneLineGuideCurrentOfferPreviewAction = (
    offerPreview: Promise<TOfferPreview>,
): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_CURRENT_OFFER_PREVIEW,
    payload: { offerPreview },
});

export const setOneLineGuideTimeStamp = (timeStamp: DateTime): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_TIME_STAMP,
    payload: { timeStamp },
});

export const setOneLineGuideAreFutureCellsReceived = (
    areFutureCellsReceived: boolean,
): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_ARE_FUTURE_CELLS_RECEIVED,
    payload: { areFutureCellsReceived },
});

export const setOneLineGuideArePastCellsReceived = (arePastCellsReceived: boolean): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_ARE_PAST_CELLS_RECEIVED,
    payload: { arePastCellsReceived },
});

export const updateOneLineGuideAiringNowTimeoutAction = (timeout: NodeJS.Timeout): AnyAction => ({
    type: UPDATE_ONE_LINE_GUIDE_AIRING_NOW_TIMEOUT,
    payload: { oneLineGuideAiringNowTimeout: timeout },
});

export const updateOneLineGuideInactivityTimeoutAction = (timeout: NodeJS.Timeout): AnyAction => ({
    type: UPDATE_ONE_LINE_GUIDE_INACTIVITY_TIMEOUT,
    payload: { oneLineGuideInactivityTimeout: timeout },
});

export const updateOneLineGuideStickinessTimeoutAction = (timeout: NodeJS.Timeout): AnyAction => ({
    type: UPDATE_ONE_LINE_GUIDE_STICKINESS_TIMEOUT,
    payload: { oneLineGuideStickinessTimeout: timeout },
});

export const setOneLineGuideTurboScrollAction = (
    value: boolean,
    direction?: InteractiveComponents.NORTH | InteractiveComponents.SOUTH,
): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_TURBO_SCROLL,
    payload: { oneLineGuideTurboScroll: value, oneLineGuideTurboScrollDirection: direction },
});

export const setOneLineGuideHorizontalScrollAction = (
    direction?: null | InteractiveComponents.EAST | InteractiveComponents.WEST,
): AnyAction => ({
    type: SET_ONE_LINE_GUIDE_HORIZONTAL_SCROLL,
    payload: { oneLineGuideHorizontalDirection: direction },
});
